import { Link } from "@/lib/router-compat";
import { ArticleTOC } from "@/components/resources/ArticleTOC";
import { formatOfferPrice } from "@/lib/offers";

type Lang = "en" | "fr";

const SOURCES = [
  { label: "Google, Gemini Notebook plans and limits", href: "https://support.google.com/gemininotebook/answer/16213268?hl=en" },
  { label: "Google blog, NotebookLM student features (Sept. 2025)", href: "https://blog.google/innovation-and-ai/models-and-research/google-labs/notebooklm-student-features/" },
  { label: "Stanford Law School AI Learning Hub, What a notebook is", href: "https://ailearninghub.law.stanford.edu/gemini-notebook/what-a-notebook-is/" },
];

const COPY = {
  en: {
    back: "Back to guides", backTo: "/learn",
    h1: "System Atlas vs NotebookLM for Studying",
    intro: "Short answer: NotebookLM (now called Gemini Notebook) is a free research notebook for asking questions across many sources. System Atlas is a revision workspace that turns one course into a structured sheet, then schedules practice on it. Use NotebookLM to explore material, Atlas to learn it for an exam.",
    sections: [
      { id: "summary", title: "The difference in one minute" },
      { id: "table", title: "Feature comparison" },
      { id: "notebooklm", title: "When NotebookLM is the better choice" },
      { id: "atlas", title: "When System Atlas is the better choice" },
      { id: "together", title: "Using both together" },
      { id: "sources", title: "Sources" },
    ],
    summary: [
      "NotebookLM starts from a set of sources you choose and answers questions with numbered citations back into them. Its Studio panel can turn those sources into reports, audio and video overviews, flashcards and quizzes.",
      "System Atlas starts from the exam. Each upload becomes a revision sheet with a fixed structure, and every practice mode (quiz, blurting, oral defense) is tied to that sheet and fed into a spaced review plan.",
    ],
    th: ["", "NotebookLM", "System Atlas"],
    rows: [
      ["Main job", "Research and Q&A across sources", "Turn a course into revision and practice"],
      ["Answers grounded in your files", "Yes, with inline citations", "Yes, from the uploaded course"],
      ["Structured revision sheet", "Study guides and reports on request", "Generated automatically for each course"],
      ["Flashcards and quizzes", "Yes, adjustable topic and difficulty", "Multiple-choice quizzes from each sheet"],
      ["Spaced review schedule", "Not built in", "Study plan with SM-2 intervals"],
      ["Recall practice beyond quizzes", "Audio overviews, learning guide", "Blurting and an oral-style defense mode"],
      ["Free plan", "Free with a Google account: 100 notebooks, 50 sources each", "2 revision sheets per day"],
      ["Paid plan", "Through Google AI Plus, Pro or Ultra", `${formatOfferPrice("monthly","en-US")} per month, 7-day trial`],
    ],
    nlmTitle: "When NotebookLM is the better choice",
    nlm: [
      "You need to search across many documents at once, such as a literature review or a thesis.",
      "You want every answer to point to the exact passage it came from.",
      "You learn well by listening and want podcast-style audio overviews.",
      "You want a free tool and already use Google.",
    ],
    atlasTitle: "When System Atlas is the better choice",
    atlas: [
      "You have a course to master for an exam and want a clean sheet to revise from, not a chat thread.",
      "You want the tool to decide what to review and when, based on your quiz results.",
      "You want to practice recall without notes, then see what you missed.",
      "You study in French or another of the nine interface languages.",
    ],
    togetherTitle: "Using both together",
    together: "A practical split: explore readings and lectures in NotebookLM, then upload the core course into System Atlas once you know what will be examined. The sheet becomes your revision base and the study plan handles the review sessions until exam day.",
    note: "NotebookLM facts were checked against Google documentation on October 23, 2026. Limits change, so check Google's plan page before deciding.",
    cta: "Turn a course into a revision sheet", ctaTo: "/",
    sourcesTitle: "Sources",
  },
  fr: {
    back: "Retour aux guides", backTo: "/fr/learn",
    h1: "System Atlas vs NotebookLM pour réviser",
    intro: "En bref : NotebookLM (désormais appelé Gemini Notebook) est un carnet de recherche gratuit pour interroger de nombreuses sources. System Atlas est un espace de révision qui transforme un cours en fiche structurée, puis planifie l'entraînement dessus. NotebookLM pour explorer, Atlas pour apprendre en vue d'un examen.",
    sections: [
      { id: "summary", title: "La différence en une minute" },
      { id: "table", title: "Comparatif des fonctionnalités" },
      { id: "notebooklm", title: "Quand choisir NotebookLM" },
      { id: "atlas", title: "Quand choisir System Atlas" },
      { id: "together", title: "Utiliser les deux" },
      { id: "sources", title: "Sources" },
    ],
    summary: [
      "NotebookLM part d'un ensemble de sources que vous choisissez et répond avec des citations numérotées qui renvoient au passage exact. Son panneau Studio peut en tirer des rapports, des résumés audio et vidéo, des flashcards et des quiz.",
      "System Atlas part de l'examen. Chaque import devient une fiche de révision à structure fixe, et chaque mode d'entraînement (quiz, blurting, défense orale) est lié à cette fiche et alimente un plan de révision espacée.",
    ],
    th: ["", "NotebookLM", "System Atlas"],
    rows: [
      ["Usage principal", "Recherche et questions sur plusieurs sources", "Transformer un cours en révision et entraînement"],
      ["Réponses fondées sur vos fichiers", "Oui, avec citations", "Oui, à partir du cours importé"],
      ["Fiche de révision structurée", "Guides d'étude et rapports à la demande", "Générée automatiquement pour chaque cours"],
      ["Flashcards et quiz", "Oui, sujet et difficulté réglables", "QCM générés depuis chaque fiche"],
      ["Planning de révision espacée", "Non intégré", "Plan de révision avec intervalles SM-2"],
      ["Rappel actif au-delà des quiz", "Résumés audio, guide d'apprentissage", "Blurting et mode défense orale"],
      ["Offre gratuite", "Gratuit avec un compte Google : 100 carnets, 50 sources chacun", "2 fiches de révision par jour"],
      ["Offre payante", "Via Google AI Plus, Pro ou Ultra", `${formatOfferPrice("monthly","fr-FR")} par mois, essai de 7 jours`],
    ],
    nlmTitle: "Quand choisir NotebookLM",
    nlm: [
      "Vous devez chercher dans beaucoup de documents à la fois, par exemple pour un mémoire.",
      "Vous voulez que chaque réponse renvoie au passage exact d'où elle vient.",
      "Vous apprenez bien à l'écoute et voulez des résumés audio façon podcast.",
      "Vous voulez un outil gratuit et utilisez déjà Google.",
    ],
    atlasTitle: "Quand choisir System Atlas",
    atlas: [
      "Vous avez un cours à maîtriser pour un examen et voulez une fiche propre, pas un fil de discussion.",
      "Vous voulez que l'outil décide quoi réviser et quand, selon vos résultats aux quiz.",
      "Vous voulez vous entraîner à restituer sans vos notes, puis voir ce qui manque.",
      "Vous révisez en français ou dans l'une des neuf langues de l'interface.",
    ],
    togetherTitle: "Utiliser les deux",
    together: "Une répartition pratique : explorez lectures et cours dans NotebookLM, puis importez le cours principal dans System Atlas quand vous savez ce qui tombera à l'examen. La fiche devient votre base de révision et le plan gère les séances jusqu'au jour J.",
    note: "Les informations sur NotebookLM ont été vérifiées dans la documentation Google le 23 octobre 2026. Les limites évoluent : consultez la page des offres Google avant de choisir.",
    cta: "Transformer un cours en fiche", ctaTo: "/fr",
    sourcesTitle: "Sources",
  },
} as const;

export function AtlasVsNotebookLMGuide({ lang }: { lang: Lang }) {
  const c = COPY[lang];
  return (
    <div className="container mx-auto px-6 py-12 max-w-3xl">
      <Link to={c.backTo} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8">← {c.back}</Link>
      <article>
        <h1 className="text-3xl md:text-4xl font-serif font-bold mb-4 tracking-tight">{c.h1}</h1>
        <p className="text-lg text-muted-foreground mb-8 leading-relaxed">{c.intro}</p>
        <ArticleTOC sections={[...c.sections]} />

        <section id="summary" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">{c.sections[0].title}</h2>
          {c.summary.map((p) => <p key={p} className="text-muted-foreground mb-3">{p}</p>)}
        </section>

        <section id="table" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">{c.sections[1].title}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-border/60 rounded-lg">
              <thead><tr className="bg-muted/30">{c.th.map((h, i) => <th key={i} className="text-left p-3 font-medium">{h}</th>)}</tr></thead>
              <tbody className="text-muted-foreground">
                {c.rows.map((r) => (
                  <tr key={r[0]} className="border-t border-border/40">
                    <td className="p-3 font-medium text-foreground">{r[0]}</td><td className="p-3">{r[1]}</td><td className="p-3">{r[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-3">{c.note}</p>
        </section>

        <section id="notebooklm" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">{c.nlmTitle}</h2>
          <ul className="list-disc pl-5 space-y-2 text-muted-foreground">{c.nlm.map((x) => <li key={x}>{x}</li>)}</ul>
        </section>

        <section id="atlas" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">{c.atlasTitle}</h2>
          <ul className="list-disc pl-5 space-y-2 text-muted-foreground">{c.atlas.map((x) => <li key={x}>{x}</li>)}</ul>
        </section>

        <section id="together" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">{c.togetherTitle}</h2>
          <p className="text-muted-foreground mb-6">{c.together}</p>
          <Link to={c.ctaTo} className="inline-flex items-center rounded-lg bg-primary px-5 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors">{c.cta}</Link>
        </section>

        <section id="sources" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">{c.sourcesTitle}</h2>
          <ul className="space-y-2 text-sm">{SOURCES.map((s) => <li key={s.href}><a href={s.href} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2">{s.label}</a></li>)}</ul>
        </section>
      </article>
    </div>
  );
}

export function AtlasVsNotebookLMGuideEn() { return <AtlasVsNotebookLMGuide lang="en" />; }
export function AtlasVsNotebookLMGuideFr() { return <AtlasVsNotebookLMGuide lang="fr" />; }

export const NOTEBOOKLM_META = {
  en: { title: "System Atlas vs NotebookLM for Studying (2026) | System Atlas", description: "NotebookLM or System Atlas for exam revision? Compare sources, flashcards, quizzes, spaced review and free plans, with Google-checked NotebookLM limits.", headline: COPY.en.h1 },
  fr: { title: "System Atlas vs NotebookLM pour réviser (2026) | System Atlas", description: "NotebookLM ou System Atlas pour réviser un examen ? Sources, flashcards, quiz, révision espacée et offres gratuites comparés, limites vérifiées chez Google.", headline: COPY.fr.h1 },
};
